import { useEffect, useState } from 'react';
import { useMutation } from 'convex/react';
import { Pencil, Check, X } from 'lucide-react';

import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/use-toast';
import { friendlyAuthError } from '@/lib/auth-errors';
import { cn } from '@/lib/utils';

interface SketchLabelEditorProps {
  sketchId: Id<'sketches'>;
  label: string;
  className?: string;
}

/**
 * Tap-to-edit label for a sketch. Shows label as plain text by default;
 * click the edit icon to swap to an Input + save/cancel controls.
 */
export function SketchLabelEditor({ sketchId, label, className }: SketchLabelEditorProps) {
  const { token } = useAuth();
  const relabel = useMutation(api.sketches.relabel);
  const { toast } = useToast();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(label);
  }, [label, editing]);

  const cancel = () => {
    setDraft(label);
    setEditing(false);
  };

  const save = async () => {
    if (!token) return;
    const trimmed = draft.trim();
    if (!trimmed || trimmed === label) {
      cancel();
      return;
    }
    setSaving(true);
    try {
      await relabel({ token, sketchId, label: trimmed });
      setEditing(false);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not rename',
        description: friendlyAuthError(err),
      });
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className={cn(
          'group inline-flex items-center gap-2 text-left',
          'focus-visible:outline-none',
          className,
        )}
      >
        <span className="font-serif text-base italic text-ink">{label}</span>
        <Pencil
          className="h-3.5 w-3.5 text-graphite opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
          aria-hidden
        />
        <span className="sr-only">Rename sketch</span>
      </button>
    );
  }

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') cancel();
        }}
        className="h-8 max-w-[220px] text-sm"
        autoFocus
        maxLength={60}
        disabled={saving}
      />
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={save} disabled={saving}>
        <Check className="h-3.5 w-3.5 text-moss" />
      </Button>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={cancel} disabled={saving}>
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
